import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import type { FC } from "react";
import { useMemo, useState } from "react";
import AsyncLoadingButton from "../../components/asyncLoadingButton/AsyncLoadingButton";
import { type SendToProvidersRequest, type SpreadsheetExportTarget } from "./participantListApi";
import { type ParticipantListStatus } from "./participantListStatus";

function targetKey(providerId: string, bookingNumber: string) {
  return `${providerId}::${bookingNumber}`;
}

interface Props {
  readonly exportTargets: SpreadsheetExportTarget[];
  readonly participantListStatus: ParticipantListStatus;
  readonly listVersionNumber: number;
  readonly sendToProviders: (listVersionNumber: number, body: SendToProvidersRequest) => Promise<unknown>;
}

const SendParticipantListToProviders: FC<Props> = ({
  exportTargets,
  participantListStatus,
  listVersionNumber,
  sendToProviders,
}) => {
  const eligibleTargets = useMemo(() => exportTargets.filter((t) => t.hasRecipientEmails), [exportTargets]);
  const eligibleKeys = useMemo(
    () => eligibleTargets.map((t) => targetKey(t.providerId, t.bookingNumber)),
    [eligibleTargets],
  );
  const eligibleKeySet = useMemo(() => new Set(eligibleKeys), [eligibleKeys]);

  const hasAnyEligibleProvider = exportTargets.some((t) => t.hasRecipientEmails);
  const canSendList = participantListStatus === "READY" && hasAnyEligibleProvider;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());

  const openDialog = () => {
    setSelectedKeys(new Set(eligibleKeys));
    setDialogOpen(true);
  };

  const closeDialog = () => setDialogOpen(false);

  const selectedEligibleCount = useMemo(() => {
    return Array.from(selectedKeys)
      .filter((k) => eligibleKeySet.has(k))
      .reduce((acc) => acc + 1, 0);
  }, [eligibleKeySet, selectedKeys]);

  const allEligibleSelected = eligibleKeys.length > 0 && selectedEligibleCount === eligibleKeys.length;
  const someEligibleSelected = selectedEligibleCount > 0 && !allEligibleSelected;

  const toggleSelectAll = () => {
    if (allEligibleSelected) {
      setSelectedKeys(new Set());
    } else {
      setSelectedKeys(new Set(eligibleKeys));
    }
  };

  const toggleOne = (key: string) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleConfirm = async () => {
    const selectedBookings = eligibleTargets
      .filter((t) => selectedKeys.has(targetKey(t.providerId, t.bookingNumber)))
      .map((t) => ({ providerId: t.providerId, bookingNumber: t.bookingNumber }));

    const body: SendToProvidersRequest =
      selectedBookings.length === eligibleTargets.length
        ? { sendToAll: true }
        : { sendToAll: false, bookings: selectedBookings };

    await sendToProviders(listVersionNumber, body);
    closeDialog();
  };

  const confirmDisabled = eligibleTargets.length === 0 || selectedEligibleCount === 0;

  return (
    <>
      <Button variant="outlined" color="primary" disabled={!canSendList} onClick={openDialog}>
        Wyślij do dostawców
      </Button>

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth aria-labelledby="send-providers-dialog">
        <DialogTitle id="send-providers-dialog">Wyślij listę do dostawców</DialogTitle>
        <DialogContent>
          <List>
            <ListItemButton onClick={toggleSelectAll} disabled={eligibleTargets.length === 0}>
              <ListItemIcon>
                <Checkbox
                  checked={allEligibleSelected}
                  indeterminate={someEligibleSelected}
                  slotProps={{ input: { "aria-label": "Zaznacz wszystkich dostawców z adresem" } }}
                />
              </ListItemIcon>
              <ListItemText primary="Zaznacz wszystkich dostawców z adresem" />
            </ListItemButton>
            <Divider />
            {exportTargets.map((t) => {
              const key = targetKey(t.providerId, t.bookingNumber);
              const eligible = t.hasRecipientEmails;
              return (
                <ListItemButton key={key} onClick={() => toggleOne(key)} disabled={!eligible}>
                  <ListItemIcon>
                    <Checkbox
                      checked={selectedKeys.has(key)}
                      disabled={!eligible}
                      slotProps={{ input: { "aria-label": `${t.providerName} – ${t.bookingNumber}` } }}
                    />
                  </ListItemIcon>
                  <ListItemText primary={`${t.providerName} – ${t.bookingNumber}`} />
                </ListItemButton>
              );
            })}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Anuluj</Button>
          <AsyncLoadingButton onClick={handleConfirm} disabled={confirmDisabled}>
            Wyślij
          </AsyncLoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SendParticipantListToProviders;
